import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, Phone, RotateCcw, PartyPopper } from 'lucide-react';
import * as playApi from '../api/playApi';
import BoxCarousel from '../components/game/BoxCarousel';
import SemicircleBoxes from '../components/game/SemicircleBoxes';
import { PrizeBoardColumn } from '../components/game/PrizeBoardSplit';
import RevealAnimation from '../components/game/RevealAnimation';
import GameHeader from '../components/game/GameHeader';
import StudioBackground from '../components/game/StudioBackground';
import Button from '../components/ui/Button';
import Input from '../components/ui/Input';

/* ── Helpers ── */
function formatEuro(value) {
  if (typeof value !== 'number') return String(value);
  return value.toLocaleString('it-IT', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: value < 1 ? 2 : 0,
    maximumFractionDigits: 2,
  });
}

/* ── Animations CSS ── */
const animCSS = `
  @keyframes finalConfetti {
    0% { transform: translate(var(--fcx, 0), -20px) rotate(0deg); opacity: 1; }
    80% { opacity: 0.8; }
    100% { transform: translate(var(--fcx2, 30px), var(--fcy, 500px)) rotate(var(--fcr, 900deg)); opacity: 0; }
  }
  @keyframes finalSpotlight {
    0%, 100% { opacity: 0.3; transform: rotate(-5deg); }
    50% { opacity: 0.6; transform: rotate(5deg); }
  }
  @keyframes introTitleGlow {
    0%, 100% { filter: drop-shadow(0 0 20px rgba(212,175,55,0.2)); }
    50% { filter: drop-shadow(0 0 40px rgba(212,175,55,0.4)); }
  }
  @keyframes headerGoldShimmer {
    0% { background-position: -200% center; }
    100% { background-position: 200% center; }
  }

  /* ── TV Show layout: blue | center | red ── */
  .tv-game-layout {
    display: grid;
    grid-template-columns: minmax(130px, 175px) 1fr minmax(130px, 175px);
    flex: 1;
    gap: 0;
    position: relative;
    z-index: 1;
  }
  .tv-board-left, .tv-board-right {
    display: flex;
    align-items: flex-start;
    justify-content: center;
    padding: 12px 6px;
    z-index: 2;
  }
  .tv-center {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 8px 4px;
    gap: 10px;
    overflow: hidden;
    z-index: 1;
    min-width: 0;
  }

  /* ── Mobile: compact everything to fit viewport ── */
  @media (max-width: 900px) {
    .tv-game-layout {
      display: flex !important;
      flex-direction: column !important;
      min-height: 0 !important;
      flex: 1;
    }
    .tv-boards-mobile {
      display: flex !important;
      gap: 4px;
      padding: 2px 4px;
      justify-content: center;
      overflow-x: auto;
      flex-shrink: 0;
    }
    .tv-board-left, .tv-board-right {
      display: none !important;
    }
    .tv-center {
      padding: 2px 4px !important;
      gap: 4px !important;
      flex: 1;
      justify-content: flex-start !important;
    }
  }
  @media (min-width: 901px) {
    .tv-boards-mobile {
      display: none !important;
    }
  }

  /* Top bar & header compact on mobile */
  @media (max-width: 768px) {
    .sp-top-bar {
      padding: 4px 8px !important;
    }
    .sp-top-bar a, .sp-top-bar button {
      font-size: 0.7rem !important;
      padding: 3px 8px !important;
    }
    .sp-msg-banner {
      font-size: 0.8rem !important;
      padding: 3px 8px !important;
    }
  }
`;

/* ── Phases ── */
const PHASE = {
  INTRO: 'intro',
  SELECT: 'select',
  PLAYING: 'playing',
  OFFER: 'offer',
  FINAL: 'final',
};

export default function SinglePlayerGame() {
  const [phase, setPhase] = useState(PHASE.INTRO);
  const [playerName, setPlayerName] = useState('');
  const [gameCode, setGameCode] = useState(() => sessionStorage.getItem('sp_game_code') || '');
  const [game, setGame] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  /* reveal overlay */
  const [revealVisible, setRevealVisible] = useState(false);
  const [revealBox, setRevealBox] = useState(null);
  const [revealPrize, setRevealPrize] = useState(null);
  const [revealLabel, setRevealLabel] = useState(null);

  /* offer */
  const [currentOffer, setCurrentOffer] = useState(null);
  const [offerResponding, setOfferResponding] = useState(false);

  /* final */
  const [finalData, setFinalData] = useState(null);

  const openingRef = useRef(false);

  /* ── Restore session ── */
  useEffect(() => {
    const code = sessionStorage.getItem('sp_game_code');
    if (code) {
      setGameCode(code);
      playApi.getGame(code)
        .then((data) => {
          setGame(data);
          if (data.status === 'completed' || data.status === 'cancelled') {
            setFinalData(data);
            setPhase(PHASE.FINAL);
          } else if (!data.player_box_number) {
            setPhase(PHASE.SELECT);
          } else if (data.last_offer && data.last_offer.status === 'pending') {
            setCurrentOffer(data.last_offer);
            setPhase(PHASE.OFFER);
          } else {
            setPhase(PHASE.PLAYING);
          }
        })
        .catch(() => {
          sessionStorage.removeItem('sp_game_code');
          setGameCode('');
          setPhase(PHASE.INTRO);
        });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  /* ── Compute round info ── */
  const currentRound = game?.current_round || 1;
  const roundInfo = game?.current_round_info;
  const boxesToOpenTotal = roundInfo?.boxes_to_open || 0;
  const boxesOpenedInRound = roundInfo?.boxes_opened || 0;
  const boxesRemaining = Math.max(0, boxesToOpenTotal - boxesOpenedInRound);
  const allPrizes = game?.all_prizes || game?.prizes || [];

  /* ── Handlers ── */
  const handleStart = async (e) => {
    e.preventDefault();
    if (!playerName.trim()) return;
    setLoading(true);
    setError('');
    try {
      const data = await playApi.startGame(playerName.trim());
      const code = data.code || data.game_code;
      setGameCode(code);
      sessionStorage.setItem('sp_game_code', code);
      setGame(data);
      setPhase(PHASE.SELECT);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Errore di avvio. Riprova.');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectBox = async (boxNumber) => {
    setLoading(true);
    setError('');
    try {
      const data = await playApi.selectBox(gameCode, boxNumber);
      setGame(data);
      setPhase(PHASE.PLAYING);
    } catch (err) {
      setError(err.response?.data?.detail || 'Errore nella selezione.');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenBox = useCallback(async (boxNumber) => {
    if (openingRef.current) return;
    openingRef.current = true;
    setError('');
    try {
      const data = await playApi.openBox(gameCode, boxNumber);
      const prize = data.opened_box?.prize_value;
      const prizeLabel = data.opened_box?.prize_label;

      setRevealBox(boxNumber);
      setRevealPrize(prize ?? 0);
      setRevealLabel(prizeLabel || null);
      setRevealVisible(true);
      setGame(data);

      if (data.game_finished || data.status === 'completed') {
        setCurrentOffer(null);
        return;
      }
      if (data.new_offer) {
        setCurrentOffer(data.new_offer);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Errore nell\'apertura.');
    } finally {
      openingRef.current = false;
    }
  }, [gameCode]);

  const handleRevealDismiss = useCallback(() => {
    setRevealVisible(false);
    setRevealBox(null);
    setRevealPrize(null);
    setRevealLabel(null);

    if (game?.game_finished || game?.status === 'completed') {
      setFinalData(game);
      setPhase(PHASE.FINAL);
    } else if (currentOffer) {
      setPhase(PHASE.OFFER);
    }
  }, [game, currentOffer]);

  const handleOfferRespond = async (accepted, swap = false) => {
    setOfferResponding(true);
    setError('');
    try {
      const swapBoxNum = swap ? currentOffer?.swap_box_number : null;
      const data = await playApi.respondOffer(gameCode, accepted, swap, swapBoxNum);
      setGame(data);
      setCurrentOffer(null);

      if (data.status === 'completed') {
        setFinalData(data);
        setPhase(PHASE.FINAL);
      } else if (data.swap_continued) {
        setPhase(PHASE.PLAYING);
      } else if (accepted) {
        setFinalData(data);
        setPhase(PHASE.FINAL);
      } else {
        setPhase(PHASE.PLAYING);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Errore nella risposta.');
    } finally {
      setOfferResponding(false);
    }
  };

  const handlePlayAgain = () => {
    sessionStorage.removeItem('sp_game_code');
    setGameCode('');
    setGame(null);
    setPlayerName('');
    setCurrentOffer(null);
    setFinalData(null);
    setError('');
    setPhase(PHASE.INTRO);
  };

  /* ── Render ── */
  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: 'var(--color-bg-primary)',
      position: 'relative',
    }}>
      <style>{animCSS}</style>
      <StudioBackground intensity={phase === PHASE.PLAYING ? 'normal' : 'high'} />

      <RevealAnimation
        isVisible={revealVisible}
        boxNumber={revealBox}
        prizeValue={revealPrize}
        prizeLabel={revealLabel}
        onComplete={handleRevealDismiss}
      />

      <AnimatePresence mode="wait">
        {/* ─── INTRO ─── */}
        {phase === PHASE.INTRO && (
          <motion.div
            key="intro"
            style={{
              minHeight: '100vh',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 'var(--space-xl)',
              textAlign: 'center',
              position: 'relative',
              zIndex: 1,
            }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
          >
            <Link
              to="/"
              style={{
                position: 'absolute', top: 16, left: 16,
                display: 'inline-flex', alignItems: 'center', gap: '6px',
                fontFamily: 'var(--font-body)', fontSize: '0.75rem',
                color: 'var(--color-text-muted)', textDecoration: 'none',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--color-primary)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--color-text-muted)'; }}
            >
              <ArrowLeft size={14} /> Home
            </Link>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <h1 style={{
                fontFamily: 'var(--font-display)',
                fontSize: 'clamp(2rem, 6vw, 3.5rem)',
                fontWeight: 700,
                letterSpacing: '0.06em',
                textTransform: 'uppercase',
                margin: 0,
                background: 'linear-gradient(90deg, #8B6914, #D4AF37, #F0D060, #FFE88A, #F0D060, #D4AF37, #8B6914)',
                backgroundSize: '300% auto',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                animation: 'introTitleGlow 3s ease-in-out infinite, headerGoldShimmer 4s linear infinite',
              }}>
                PickBoxGame
              </h1>
              <p style={{
                fontFamily: 'var(--font-body)',
                fontSize: '1.05rem',
                color: 'var(--color-text-secondary)',
                marginTop: 'var(--space-md)',
                marginBottom: 'var(--space-xl)',
                maxWidth: '440px',
              }}>
                Inserisci il tuo nome e preparati a sfidare la fortuna.
              </p>

              <form onSubmit={handleStart} style={{
                display: 'flex', flexDirection: 'column',
                alignItems: 'center', gap: 'var(--space-md)',
                width: '100%', maxWidth: '360px',
              }}>
                <Input
                  placeholder="Il tuo nome"
                  value={playerName}
                  onChange={(e) => setPlayerName(e.target.value)}
                  required
                  error={error}
                  style={{ textAlign: 'center', fontSize: '1.1rem' }}
                />
                <Button
                  type="submit" size="lg" loading={loading}
                  disabled={!playerName.trim()}
                  style={{ width: '100%', borderRadius: 'var(--radius-full)', letterSpacing: '0.08em' }}
                >
                  Inizia
                </Button>
              </form>
            </motion.div>
          </motion.div>
        )}

        {/* ─── BOX SELECTION (CAROUSEL) ─── */}
        {phase === PHASE.SELECT && game && (
          <motion.div
            key="select"
            style={{
              minHeight: '100vh',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 'var(--space-lg)',
              position: 'relative',
              zIndex: 1,
            }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
          >
            <BoxCarousel
              boxes={game.boxes || []}
              onBoxClick={handleSelectBox}
              disabled={loading}
            />
            {error && (
              <p style={{ color: 'var(--color-danger)', fontSize: '0.9rem', margin: '12px 0 0' }}>{error}</p>
            )}
          </motion.div>
        )}

        {/* ─── PLAYING (TV SHOW LAYOUT) ─── */}
        {phase === PHASE.PLAYING && game && (
          <motion.div
            key="playing"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            style={{ display: 'flex', flexDirection: 'column', height: '100dvh', overflow: 'auto' }}
          >
            {/* Top bar */}
            <div className="sp-top-bar" style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '6px 16px',
              backgroundColor: 'rgba(5,5,20,0.85)',
              borderBottom: '1px solid rgba(212,175,55,0.06)',
              position: 'relative',
              zIndex: 10,
            }}>
              <Link
                to="/"
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: '6px',
                  fontFamily: 'var(--font-display)', fontSize: '0.8rem', fontWeight: 600,
                  color: '#D4AF37', textDecoration: 'none', padding: '6px 14px',
                  borderRadius: '8px', background: 'rgba(212,175,55,0.08)',
                  border: '1px solid rgba(212,175,55,0.2)', transition: 'all 0.15s ease',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(212,175,55,0.15)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(212,175,55,0.08)'; }}
              >
                <ArrowLeft size={14} /> Home
              </Link>
              <button
                onClick={handlePlayAgain}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: '6px',
                  fontFamily: 'var(--font-display)', fontSize: '0.8rem', fontWeight: 600,
                  color: '#e74c3c', background: 'rgba(231,76,60,0.08)',
                  border: '1px solid rgba(231,76,60,0.2)', borderRadius: '8px',
                  padding: '6px 14px', cursor: 'pointer', transition: 'all 0.15s ease',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(231,76,60,0.15)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(231,76,60,0.08)'; }}
              >
                <RotateCcw size={13} /> Ricomincia
              </button>
            </div>

            {/* Header */}
            <GameHeader game={game} />

            {/* Mobile boards row (visible only on mobile) */}
            <div className="tv-boards-mobile">
              <PrizeBoardColumn boxes={game.boxes || []} allPrizes={allPrizes} side="low" />
              <PrizeBoardColumn boxes={game.boxes || []} allPrizes={allPrizes} side="high" />
            </div>

            {/* ── TV Show 3-column layout ── */}
            <div className="tv-game-layout">
              {/* Left: Blue board */}
              <div className="tv-board-left">
                <PrizeBoardColumn boxes={game.boxes || []} allPrizes={allPrizes} side="low" />
              </div>

              {/* Center: Semicircle boxes + message */}
              <div className="tv-center">
                {/* Instruction message */}
                <motion.div
                  className="sp-msg-banner"
                  style={{
                    fontFamily: 'var(--font-display)',
                    fontSize: '1.05rem',
                    fontWeight: 600,
                    color: 'var(--color-primary)',
                    textAlign: 'center',
                    letterSpacing: '0.04em',
                    padding: 'var(--space-xs) var(--space-lg)',
                    backgroundColor: 'rgba(212,175,55,0.06)',
                    borderRadius: 'var(--radius-md)',
                    border: '1px solid rgba(212,175,55,0.12)',
                    width: '100%',
                    maxWidth: '580px',
                  }}
                  key={`msg-${currentRound}-${boxesRemaining}`}
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  {boxesRemaining > 0
                    ? `Apri ${boxesRemaining} scatol${boxesRemaining === 1 ? 'a' : 'e'} in questo turno`
                    : 'Turno completato!'}
                </motion.div>

                {/* Semicircle boxes + player box */}
                <SemicircleBoxes
                  boxes={game.boxes || []}
                  playerBoxNumber={game.player_box_number}
                  onBoxClick={handleOpenBox}
                  disabled={loading || revealVisible || boxesRemaining <= 0}
                />

                {error && (
                  <p style={{ color: 'var(--color-danger)', fontSize: '0.9rem', margin: 0 }}>{error}</p>
                )}

                {/* Last offer rejected info */}
                {game.last_offer && game.last_offer.status === 'rejected' && (
                  <div style={{
                    fontFamily: 'var(--font-mono)',
                    fontSize: '0.8rem',
                    color: 'var(--color-danger)',
                    backgroundColor: 'rgba(231,76,60,0.1)',
                    padding: '5px 14px',
                    borderRadius: 'var(--radius-full)',
                    textDecoration: 'line-through',
                    opacity: 0.7,
                  }}>
                    Ultima offerta rifiutata: {formatEuro(game.last_offer.amount)}
                  </div>
                )}
              </div>

              {/* Right: Red board */}
              <div className="tv-board-right">
                <PrizeBoardColumn boxes={game.boxes || []} allPrizes={allPrizes} side="high" />
              </div>
            </div>
          </motion.div>
        )}

        {/* ─── OFFER ─── */}
        {phase === PHASE.OFFER && currentOffer && (
          <motion.div
            key="offer"
            style={{
              position: 'fixed', inset: 0, zIndex: 900,
              display: 'flex', flexDirection: 'column',
              alignItems: 'center', justifyContent: 'center',
              gap: 'var(--space-xl)', padding: 'var(--space-xl)',
              background: `
                radial-gradient(ellipse 60% 50% at 50% 40%, rgba(212,175,55,0.06) 0%, transparent 70%),
                radial-gradient(ellipse 80% 60% at 50% 100%, rgba(139,69,19,0.04) 0%, transparent 50%),
                rgba(2,2,10,0.94)
              `,
              backdropFilter: 'blur(12px)',
            }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
          >
            {currentOffer.swap_only ? (
              <>
                <motion.div
                  style={{
                    display: 'flex', alignItems: 'center', gap: 'var(--space-sm)',
                    fontFamily: 'var(--font-display)', fontSize: '1.1rem', fontWeight: 600,
                    color: 'var(--color-text-secondary)', letterSpacing: '0.1em', textTransform: 'uppercase',
                  }}
                  initial={{ opacity: 0, y: -20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                >
                  <Phone size={20} />
                  Il Banchiere ti propone lo SCAMBIO!
                </motion.div>

                <motion.div
                  style={{
                    fontFamily: 'var(--font-display)', fontSize: 'clamp(1.2rem, 3vw, 1.8rem)',
                    fontWeight: 600, color: 'var(--color-text-primary)', textAlign: 'center', lineHeight: 1.5,
                  }}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.6 }}
                >
                  Vuoi scambiare la tua scatola con la scatola N. {currentOffer.swap_box_number}
                  {(() => {
                    const regionName = currentOffer.swap_region_name
                      || game?.boxes?.find(b => b.box_number === currentOffer.swap_box_number)?.region_name;
                    return regionName ? ` (${regionName})` : '';
                  })()}?
                </motion.div>

                <motion.div
                  style={{ display: 'flex', gap: 'var(--space-lg)', flexWrap: 'wrap', justifyContent: 'center' }}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 1 }}
                >
                  <Button size="lg" loading={offerResponding} onClick={() => handleOfferRespond(false, true)}
                    style={{ backgroundColor: 'var(--color-info)', borderColor: 'var(--color-info)', color: '#fff', padding: '16px 48px', fontSize: '1.1rem', borderRadius: 'var(--radius-full)' }}>
                    Scambio!
                  </Button>
                  <Button size="lg" loading={offerResponding} onClick={() => handleOfferRespond(false, false)}
                    style={{ backgroundColor: 'var(--color-primary)', borderColor: 'var(--color-primary)', color: 'var(--color-bg-primary)', padding: '16px 48px', fontSize: '1.1rem', borderRadius: 'var(--radius-full)' }}>
                    Tengo il mio!
                  </Button>
                </motion.div>
              </>
            ) : (
              <>
                <motion.div
                  style={{
                    display: 'flex', alignItems: 'center', gap: 'var(--space-sm)',
                    fontFamily: 'var(--font-display)', fontSize: '1.1rem', fontWeight: 600,
                    color: 'var(--color-text-secondary)', letterSpacing: '0.1em', textTransform: 'uppercase',
                  }}
                  initial={{ opacity: 0, y: -20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                >
                  <Phone size={20} />
                  Il Banchiere propone
                </motion.div>

                <motion.div
                  style={{
                    fontFamily: 'var(--font-display)', fontSize: 'clamp(3rem, 8vw, 5rem)', fontWeight: 700,
                    color: 'var(--color-primary)', textShadow: '0 0 50px rgba(212,175,55,0.5)',
                  }}
                  initial={{ scale: 0, rotate: -10 }}
                  animate={{ scale: 1, rotate: 0 }}
                  transition={{ type: 'spring', stiffness: 180, damping: 14, delay: 0.6 }}
                >
                  {formatEuro(currentOffer.amount)}
                </motion.div>

                <motion.div
                  style={{ display: 'flex', gap: 'var(--space-lg)', flexWrap: 'wrap', justifyContent: 'center' }}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 1 }}
                >
                  <Button size="lg" loading={offerResponding} onClick={() => handleOfferRespond(true)}
                    style={{ backgroundColor: 'var(--color-success)', borderColor: 'var(--color-success)', color: '#fff', padding: '16px 48px', fontSize: '1.1rem', borderRadius: 'var(--radius-full)' }}>
                    Accetto
                  </Button>
                  <Button size="lg" loading={offerResponding} onClick={() => handleOfferRespond(false)}
                    style={{ backgroundColor: 'var(--color-danger)', borderColor: 'var(--color-danger)', color: '#fff', padding: '16px 48px', fontSize: '1.1rem', borderRadius: 'var(--radius-full)' }}>
                    Rifiuto
                  </Button>
                </motion.div>
              </>
            )}
          </motion.div>
        )}

        {/* ─── FINAL ─── */}
        {phase === PHASE.FINAL && (
          <motion.div
            key="final"
            style={{
              position: 'fixed', inset: 0, zIndex: 950,
              display: 'flex', flexDirection: 'column',
              alignItems: 'center', justifyContent: 'center',
              gap: 'var(--space-lg)', padding: 'var(--space-xl)', textAlign: 'center',
              background: `
                radial-gradient(ellipse 50% 40% at 50% 45%, rgba(212,175,55,0.08) 0%, transparent 70%),
                radial-gradient(ellipse 80% 50% at 50% 100%, rgba(212,175,55,0.04) 0%, transparent 40%),
                rgba(2,2,10,0.95)
              `,
              overflow: 'hidden',
            }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5 }}
          >
            {/* Confetti */}
            {Array.from({ length: 40 }, (_, i) => (
              <div key={`conf-${i}`} style={{
                position: 'absolute', top: '-10px',
                left: `${Math.random() * 100}%`,
                width: `${4 + Math.random() * 6}px`,
                height: `${3 + Math.random() * 5}px`,
                borderRadius: Math.random() > 0.5 ? '50%' : '1px',
                backgroundColor: ['#D4AF37', '#F0D060', '#e74c3c', '#3498db', '#2ecc71', '#FFD700'][Math.floor(Math.random() * 6)],
                animation: `finalConfetti ${2.5 + Math.random() * 2}s ease-in ${Math.random() * 2}s forwards`,
                '--fcx': `${(Math.random() - 0.5) * 120}px`,
                '--fcx2': `${(Math.random() - 0.5) * 180}px`,
                '--fcy': `${400 + Math.random() * 300}px`,
                '--fcr': `${(Math.random() - 0.5) * 1440}deg`,
                opacity: 0, pointerEvents: 'none', zIndex: 5,
              }} />
            ))}

            {/* Spotlight */}
            <div style={{
              position: 'absolute', top: '-20%', left: '30%', width: '40%', height: '70%',
              background: 'radial-gradient(ellipse at center, rgba(212,175,55,0.06) 0%, transparent 60%)',
              animation: 'finalSpotlight 5s ease-in-out infinite', pointerEvents: 'none',
            }} />

            <motion.div
              style={{ color: 'var(--color-primary)', marginBottom: 'var(--space-sm)' }}
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: 'spring', stiffness: 200, damping: 12, delay: 0.2 }}
            >
              <PartyPopper size={64} />
            </motion.div>

            <motion.h2
              style={{
                fontFamily: 'var(--font-display)', fontSize: 'clamp(1.5rem, 4vw, 2.5rem)',
                fontWeight: 700, color: 'var(--color-text-primary)', margin: 0,
                letterSpacing: '0.04em', textTransform: 'uppercase',
              }}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
            >
              Partita terminata!
            </motion.h2>

            {(() => {
              const fd = finalData || game;
              if (!fd) return null;
              const didAcceptOffer = fd.offer_accepted === true;
              const didSwap = fd.swapped === true;
              const finalPrize = fd.final_prize_value;
              const playerBoxPrize = fd.player_box_prize_value;
              const lastOffer = fd.last_offer;

              const prizeStyle = {
                fontFamily: 'var(--font-display)', fontSize: 'clamp(3rem, 8vw, 5rem)',
                fontWeight: 700, color: 'var(--color-primary)', margin: 0,
                textShadow: '0 0 60px rgba(212,175,55,0.5)',
              };
              const detailStyle = {
                fontFamily: 'var(--font-body)', fontSize: '1.1rem',
                color: 'var(--color-text-secondary)', margin: 0, lineHeight: 1.7,
              };

              if (didSwap) {
                return (
                  <>
                    <motion.p style={detailStyle} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.6 }}>
                      Hai scambiato la scatola! La tua nuova scatola conteneva
                    </motion.p>
                    <motion.div style={prizeStyle} initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: 'spring', stiffness: 150, damping: 12, delay: 0.8 }}>
                      {formatEuro(finalPrize ?? playerBoxPrize ?? 0)}
                    </motion.div>
                  </>
                );
              }
              if (didAcceptOffer && lastOffer) {
                return (
                  <>
                    <motion.p style={detailStyle} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.6 }}>
                      Hai accettato l'offerta del Banchiere
                    </motion.p>
                    <motion.div style={prizeStyle} initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: 'spring', stiffness: 150, damping: 12, delay: 0.8 }}>
                      {formatEuro(finalPrize ?? lastOffer.amount)}
                    </motion.div>
                    {playerBoxPrize !== undefined && playerBoxPrize !== null && (
                      <motion.p style={{ ...detailStyle, marginTop: 'var(--space-md)' }} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.2 }}>
                        La tua scatola conteneva <strong style={{ color: 'var(--color-primary)' }}>{formatEuro(playerBoxPrize)}</strong>
                      </motion.p>
                    )}
                  </>
                );
              }
              return (
                <>
                  <motion.p style={detailStyle} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.6 }}>
                    La tua scatola conteneva
                  </motion.p>
                  <motion.div style={prizeStyle} initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: 'spring', stiffness: 150, damping: 12, delay: 0.8 }}>
                    {formatEuro(finalPrize ?? playerBoxPrize ?? 0)}
                  </motion.div>
                </>
              );
            })()}

            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.5 }}>
              <Button size="lg" onClick={handlePlayAgain}
                style={{ marginTop: 'var(--space-lg)', borderRadius: 'var(--radius-full)', padding: '14px 40px', letterSpacing: '0.08em' }}>
                <RotateCcw size={18} /> Gioca ancora
              </Button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
